import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiMapPin, FiClock, FiCheckCircle, FiInfo, FiNavigation } from 'react-icons/fi';
import { FaTrain } from 'react-icons/fa';
import Card from '../components/Common/Card';
import Button from '../components/Common/Button';
import styles from './HomePage.module.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <FiSearch />,
      title: 'PNR Status Check',
      description: 'Get instant updates on your PNR status with detailed passenger information.',
      action: () => navigate('/pnr-status'),
      color: 'primary',
    },
    {
      icon: <FiMapPin />,
      title: 'Live Train Status',
      description: 'Track your train in real-time with accurate station-wise updates.',
      action: () => navigate('/train-status'),
      color: 'secondary',
    },
    {
      icon: <FaTrain />,
      title: 'Train Information',
      description: 'Get complete train details including route, schedule, and stations.',
      action: () => navigate('/train-info'),
      color: 'primary',
    },
    {
      icon: <FiNavigation />,
      title: 'Live at Station',
      description: 'Check all trains arriving and departing from any station in real-time.',
      action: () => navigate('/live-station'),
      color: 'secondary',
    },
    {
      icon: <FiInfo />,
      title: 'Search Trains',
      description: 'Find all available trains between any two stations with timings.',
      action: () => navigate('/search-trains'),
      color: 'primary',
    },
  ];

  const highlights = [
    { icon: <FiClock />, text: 'Real-time Updates' },
    { icon: <FiCheckCircle />, text: 'Accurate Information' },
    { icon: <FiSearch />, text: 'Easy to Use' },
    { icon: <FiMapPin />, text: 'Complete Journey Details' },
  ];

  return (
    <div className={styles.homePage}>
      <div className="container">
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              Track Your Train Journey
              <span className={styles.heroTitleAccent}> Instantly</span>
            </h1>
            <p className={styles.heroDescription}>
              Check PNR status, live train running status, train info, and search trains between stations.
              Fast, reliable, and always accurate.
            </p>
            
            {/* Highlights */}
            <div className={styles.highlights}>
              {highlights.map((highlight, index) => (
                <div key={index} className={styles.highlight}>
                  <div className={styles.highlightIcon}>{highlight.icon}</div>
                  <span className={styles.highlightText}>{highlight.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.heroImage}>
            <div className={styles.trainAnimation}>
              <div className={styles.track}></div>
              <div className={styles.train}>🚄</div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className={styles.featuresSection}>
          <h2 className={styles.sectionTitle}>Our Services</h2>
          <div className={styles.featuresGrid}>
            {features.map((feature, index) => (
              <Card key={index} hover className={styles.featureCard}>
                <div className={`${styles.featureIcon} ${styles[feature.color]}`}>
                  {feature.icon}
                </div>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureDescription}>{feature.description}</p>
                <Button
                  variant={feature.color === 'primary' ? 'primary' : 'secondary'}
                  onClick={feature.action}
                  fullWidth
                >
                  Get Started
                </Button>
              </Card>
            ))}
          </div>
        </section>

        {/* Stats Section */}
        <section className={styles.statsSection}>
          <Card className={styles.statsCard}>
            <div className={styles.statsGrid}>
              <div className={styles.stat}>
                <div className={styles.statNumber}>1M+</div>
                <div className={styles.statLabel}>Queries Processed</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statNumber}>99.9%</div>
                <div className={styles.statLabel}>Uptime</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statNumber}>24/7</div>
                <div className={styles.statLabel}>Availability</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statNumber}>{'<'}2s</div>
                <div className={styles.statLabel}>Avg Response Time</div>
              </div>
            </div>
          </Card>
        </section>

        {/* How It Works */}
        <section className={styles.howItWorksSection}>
          <h2 className={styles.sectionTitle}>How It Works</h2>
          <div className={styles.stepsGrid}>
            <div className={styles.step}>
              <div className={styles.stepNumber}>1</div>
              <h3 className={styles.stepTitle}>Choose Service</h3>
              <p className={styles.stepDescription}>Select the service you need from our options</p>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>2</div>
              <h3 className={styles.stepTitle}>Enter Details</h3>
              <p className={styles.stepDescription}>Provide required information (PNR, train number, etc.)</p>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>3</div>
              <h3 className={styles.stepTitle}>Get Results</h3>
              <p className={styles.stepDescription}>View real-time information instantly</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HomePage;